import React, { useState } from 'react';
import {
  Container, Paper, Typography, Button, Box, Grid, Card,
  CardActionArea, Divider, Chip,
  FormControl, Select, MenuItem
} from '@mui/material';
import {
  ArrowForward, ArrowBack, AccountBalance, CheckCircle, TrendingUp,
  VerifiedUser, Star, FlashOn
} from '@mui/icons-material';
import SEOHead from '../components/SEOHead';
import ServiceContactForm from '../components/ServiceContactForm';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const taxPlanningServicesList = [
  { id: 'all', name: 'All Services', icon: <FlashOn /> },
  { id: 'salaried-plan', name: 'Salaried Tax Optimization', popular: true, docs: ['Form 16', 'Rent Receipts', 'Investment Proofs'] },
  { id: 'freelance-plan', name: 'Freelancer 44ADA Advisory', popular: true, docs: ['Bank Statements', 'Form 26AS & AIS', 'Invoices'] },
  { id: 'business-plan', name: 'Business Expense Restructuring', docs: ['Audited P&L', 'Trial Balance', 'GST Returns'] },
  { id: 'hnw-plan', name: 'HNW Surcharge & Wealth Planning', docs: ['Capital Gain Statements', 'Demats Statement', 'Asset Portfolio'] },
  { id: 'nri-plan', name: 'NRI Tax & DTAA Treaty Advisory', docs: ['Passport Copies', 'NRE/NRO Bank Statements', 'Foreign Income Proof'] },
  { id: 'property-plan', name: 'Real Estate Capital Gains Saving', docs: ['Property Sale Deed', 'Purchase Deed', 'Construction Bills'] }
];

const planFeatures: Record<string, string[]> = {
  basic: ["Old vs New Tax Regime comparative analysis", "80C/80D/HRA tax saving checklist", "Email tax report"],
  standard: ["Includes Basic Package", "Section 44AD/44ADA presumptive evaluation", "1-on-1 Senior CA Tax Strategy session", "Capital Gains tax saving advisory"],
  premium: ["Includes Standard Package", "Comprehensive Wealth & Estate Planning", "HNW Surcharge reduction structuring", "Dedicated Senior Partner CA Retainer"]
};

interface Option { label: string; value: string; subtext?: string; icon: React.ReactNode; }
interface Question { id: number; text: string; description: string; options: Option[]; }

const TaxPlanning: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [showRegistration, setShowRegistration] = useState<boolean>(false);
  const [recommendedService, setRecommendedService] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<'basic' | 'standard' | 'premium'>('standard');

  const questions: Question[] = [
    {
      id: 1,
      text: "What is your primary income stream for Tax Planning?",
      description: "Tax deduction rules & optimal regime selection vary across income categories.",
      options: [
        { label: "Salaried Employee (Form 16)", value: "salary", subtext: "Optimize HRA, 80C, 80D, LTA & NPS deductions", icon: <AccountBalance sx={{ fontSize: 36 }} /> },
        { label: "Freelancer / Consultant (Section 44ADA)", value: "freelance", subtext: "Declare 50% flat income under Presumptive Taxation", icon: <TrendingUp sx={{ fontSize: 36 }} /> },
        { label: "Business Owner / Enterprise", value: "business", subtext: "Corporate tax rates, depreciation & expense optimization", icon: <Star sx={{ fontSize: 36 }} /> },
        { label: "High Net Worth Individual (HNW) / Capital Gains", value: "hnw", subtext: "Stocks, Mutual Funds, Crypto & Property capital gains", icon: <VerifiedUser sx={{ fontSize: 36 }} /> }
      ]
    },
    {
      id: 2,
      text: "What is your estimated Annual Gross Income?",
      description: "Income slab dictates surcharge applicability (up to 25%) and tax-saving urgency.",
      options: [
        { label: "Below ₹10 Lakhs", value: "below_10l", subtext: "Standard deductions & basic regime choice", icon: <AccountBalance sx={{ fontSize: 36 }} /> },
        { label: "₹10 Lakhs to ₹50 Lakhs", value: "10l_50l", subtext: "High tax savings potential across investments", icon: <TrendingUp sx={{ fontSize: 36 }} /> },
        { label: "Above ₹50 Lakhs (Surcharge Slab)", value: "above_50l", subtext: "Requires active surcharge mitigation strategy", icon: <Star sx={{ fontSize: 36 }} /> }
      ]
    },
    {
      id: 3,
      text: "Do you have significant Capital Gains from Stock/Property sales?",
      description: "Section 54, 54F & 54EC capital gains exemptions can save lakhs in tax.",
      options: [
        { label: "Yes, Property or Stock/Crypto sales this year", value: "yes_gains", subtext: "Requires capital gain exemption planning", icon: <Star sx={{ fontSize: 36 }} /> },
        { label: "No, regular income only", value: "no_gains", subtext: "Focus on income tax regime optimization", icon: <CheckCircle sx={{ fontSize: 36 }} /> }
      ]
    }
  ];

  const handleOptionSelect = (value: string) => {
    setAnswers(prev => ({ ...prev, [questions[currentStep].id]: value }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      const service = calculateRecommendedService();
      setRecommendedService(service);
      setShowRegistration(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const calculateRecommendedService = (): string => {
    const q1 = answers[1];
    const q2 = answers[2];
    const q3 = answers[3];

    if (q3 === 'yes_gains') return 'Real Estate Capital Gains Saving';
    if (q2 === 'above_50l' || q1 === 'hnw') return 'Premium HNW Advisory';
    if (q1 === 'freelance' || q1 === 'business') return 'Standard Tax Advisory';
    return 'Basic Tax Planning';
  };

  const activeServiceKey = recommendedService || 'Basic Tax Planning';

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <SEOHead
        title="Tax Planning & Advisory Services | Fingard Partners"
        description="Strategic tax planning for salaried individuals, freelancers, HNIs, NRIs, and businesses. Maximize savings with Fingard Partners' expert CA advisors."
        keywords="Tax Planning India, Tax Saving Tips, HNI Tax Advisory, NRI Tax Services, 44ADA Tax Planning, Capital Gains Tax"
        canonicalUrl="/tax-planning"
      />
      {/* Header */}
      <Box textAlign="center" sx={{ mb: 4 }}>
        <Chip label="Tax Optimization & Advisory Hub" color="primary" sx={{ mb: 1.5, fontWeight: 'bold' }} />
        <Typography variant="h4" fontWeight={800} color="#123D6B" gutterBottom>
          Personal & Corporate Tax Planning Portal
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 650, mx: 'auto' }}>
          Evaluate your tax deductions, explore Old vs New regime efficiency, and legally minimize your tax burden.
        </Typography>
      </Box>

      {/* DIAGNOSTIC WIZARD SECTION */}
      <Container maxWidth="md">
        {!showRegistration ? (
          <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, bgcolor: '#FFFFFF' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold">
                Question {currentStep + 1} of {questions.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Math.round(((currentStep + 1) / questions.length) * 100)}% Completed
              </Typography>
            </Box>

            <Typography variant="h5" fontWeight={800} color="#123D6B" sx={{ mb: 1 }}>
              {questions[currentStep].text}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              {questions[currentStep].description}
            </Typography>

            <Grid container spacing={2}>
              {questions[currentStep].options.map((opt, i) => {
                const isSelected = answers[questions[currentStep].id] === opt.value;
                return (
                  <Grid item xs={12} key={i}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 3,
                        borderColor: isSelected ? '#123D6B' : '#E2E8F0',
                        borderWidth: isSelected ? 2 : 1,
                        bgcolor: isSelected ? 'rgba(18, 61, 107, 0.04)' : '#FFFFFF',
                        transition: 'all 0.2s',
                        '&:hover': { borderColor: '#C59B27', transform: 'translateY(-2px)' }
                      }}
                    >
                      <CardActionArea onClick={() => handleOptionSelect(opt.value)} sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                          <Box sx={{ color: isSelected ? '#123D6B' : '#64748b' }}>
                            {opt.icon}
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" fontWeight="bold" color="#0f172a" sx={{ fontSize: '1.05rem' }}>
                              {opt.label}
                            </Typography>
                            {opt.subtext && (
                              <Typography variant="body2" color="text.secondary">
                                {opt.subtext}
                              </Typography>
                            )}
                          </Box>
                          {isSelected && <CheckCircle color="primary" />}
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                disabled={currentStep === 0}
                onClick={handleBack}
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                disabled={!answers[questions[currentStep].id]}
                onClick={handleNext}
                endIcon={<ArrowForward />}
                sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2, bgcolor: '#123D6B', px: 4, '&:hover': { bgcolor: '#C59B27' } }}
              >
                {currentStep === questions.length - 1 ? 'See Recommendation' : 'Next Step'}
              </Button>
            </Box>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
            <Paper elevation={3} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, bgcolor: '#FFFFFF' }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Chip label="AI Diagnostic Match" color="primary" sx={{ mb: 1.5, fontWeight: 'bold' }} />
                <Typography variant="h5" fontWeight={800} color="#123D6B" gutterBottom>
                  Recommended Solution: {activeServiceKey}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Based on your income profile, <strong>{activeServiceKey}</strong> is identified as your optimal tax saving strategy.
                </Typography>

                {/* SERVICE OVERRIDE DROPDOWN */}
                <Box sx={{ maxWidth: 480, mx: 'auto', textAlign: 'left', mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>
                    Want a different advisory service? Choose from dropdown:
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      value={activeServiceKey}
                      onChange={(e) => setRecommendedService(e.target.value)}
                      sx={{ borderRadius: 2.5, bgcolor: '#F8FAFC', fontWeight: 600, color: '#123D6B' }}
                    >
                      <MenuItem value="Basic Tax Planning">Salaried Individual Tax Planning</MenuItem>
                      <MenuItem value="Standard Tax Advisory">Freelance & Business Tax Advisory</MenuItem>
                      <MenuItem value="Premium HNW Advisory">HNW & Surcharge Mitigation Advisory</MenuItem>
                      <MenuItem value="NRI Tax & DTAA Treaty Advisory">NRI Foreign Income & DTAA Advisory</MenuItem>
                      <MenuItem value="Real Estate Capital Gains Saving">Real Estate & Stock Capital Gains Strategy</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Package Tiers Selection (Scope only, no public prices) */}
              <Typography variant="subtitle1" fontWeight="bold" color="#0f172a" sx={{ mb: 1.5 }}>
                Select Advisory Scope:
              </Typography>

              <Grid container spacing={2} sx={{ mb: 1 }}>
                {(['basic', 'standard', 'premium'] as const).map((plan) => {
                  const isSel = selectedPlan === plan;
                  return (
                    <Grid item xs={12} sm={4} key={plan}>
                      <Card
                        variant="outlined"
                        onClick={() => setSelectedPlan(plan)}
                        sx={{
                          p: 2.5,
                          cursor: 'pointer',
                          borderRadius: 3,
                          borderColor: isSel ? '#123D6B' : '#E2E8F0',
                          borderWidth: isSel ? 2 : 1,
                          bgcolor: isSel ? 'rgba(18, 61, 107, 0.04)' : '#FFFFFF',
                          textAlign: 'center',
                          position: 'relative'
                        }}
                      >
                        {plan === 'standard' && (
                          <Chip label="Most Popular" color="secondary" size="small" sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', fontWeight: 'bold' }} />
                        )}
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ textTransform: 'uppercase', mt: 1 }}>
                          {plan} Package
                        </Typography>
                        <Box sx={{ my: 1.5 }}>
                          <Chip label="Custom CA Quote" size="small" sx={{ bgcolor: '#EFF6FF', color: '#1E40AF', fontWeight: 700 }} />
                        </Box>
                        <Divider sx={{ my: 1.5 }} />
                        {planFeatures[plan].map((feat, idx) => (
                          <Typography key={idx} variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5, textAlign: 'left' }}>
                            ✓ {feat}
                          </Typography>
                        ))}
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>

            {/* Service Contact & Quotation Request Form */}
            <ServiceContactForm
              serviceCategory="tax-planning"
              selectedService={activeServiceKey + ' (' + selectedPlan.toUpperCase() + ' Scope)'}
              selectedPackageTier={selectedPlan}
              diagnosticAnswers={answers}
              onBackToQuiz={() => setShowRegistration(false)}
            />
          </Box>
        )}
      </Container>
    </Container>
  );
};

export default TaxPlanning;
